import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { CocoEvent, useAnalytics, useTracking } from '@cotera/client/app/etc';
import { Text } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  ResourcePageShell,
  useResourceBreadcrumbs,
} from '../resources/resource-page-shell';
import { Input } from '@cotera/client/v0/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import {
  ModelScopeFactory,
  useAction,
  useActionsContext,
  useInScopeModel,
} from '@cotera/client/v0/actions/framework';
import { ArchiveResourceAction } from '../resources/actions/archive-resource.action';
import { FavoriteToggleButton } from '../resources/favorites';
import { SchedulePicker } from './schedule-picker';
import { useAgentOptions, useScheduledWorkflow } from './hooks';
import {
  ResourceSelectionModel,
  ResourceType,
} from '../resources/selection/resource-selection.model';
import { Assert } from '@cotera/utilities';

const TriggerDetailResourceSelectionSync: React.FC<{
  triggerResourceId: string;
  triggerName: string;
}> = ({ triggerResourceId, triggerName }) => {
  const selection = useInScopeModel(ResourceSelectionModel);
  const actionsContext = useActionsContext();

  useEffect(() => {
    if (selection === null) {
      return;
    }
    selection.setSelections([
      {
        resourceType: ResourceType.WorkflowTrigger,
        resourceId: triggerResourceId,
        name: triggerName,
      },
    ]);
    actionsContext.targetModel(selection);
  }, [selection, triggerResourceId, triggerName, actionsContext]);

  return null;
};

const TriggerDetailLoaded: React.FC<{
  workflowId: string;
  initialName: string;
  initialSchedule: string;
  initialAgentId: string;
  initialPrompt: string;
}> = ({
  workflowId,
  initialName,
  initialSchedule,
  initialAgentId,
  initialPrompt,
}) => {
  const org = useWhoami((s) => s.org);
  const queryClient = useQueryClient();
  const client = useTenantedClient();
  const archiveAction = useAction(ArchiveResourceAction);
  const analytics = useAnalytics();

  const [name, setName] = useState(initialName);
  const [schedule, setSchedule] = useState(initialSchedule);
  const [agentId, setAgentId] = useState(initialAgentId);
  const [prompt, setPrompt] = useState(initialPrompt);
  const [saving, setSaving] = useState(false);
  const [firing, setFiring] = useState(false);

  const agents = useAgentOptions();

  const trimmedName = name.trim();
  const canSave = trimmedName !== '';

  const handleSave = useCallback(async () => {
    if (!canSave) {
      return;
    }
    setSaving(true);
    try {
      const res = await client.resource.workflow.updateScheduledAgentRun({
        id: workflowId,
        name: trimmedName,
        schedule,
        agentId,
        ...(prompt !== '' ? { prompt } : { prompt: '' }),
      });
      Assert.assertOk(res);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.workflow.scheduledAgentBundle({
          orgId: org.id,
          workflowId,
        }),
      });
    } finally {
      setSaving(false);
    }
  }, [
    workflowId,
    client,
    trimmedName,
    schedule,
    agentId,
    prompt,
    queryClient,
    org.id,
    canSave,
  ]);

  const handleFire = useCallback(async () => {
    setFiring(true);
    try {
      const res = await client.resource.workflow.runScheduledAgentNow({
        id: workflowId,
      });
      Assert.assertOk(res);
      analytics.track(CocoEvent.TriggerManuallyRun, { workflowId });
    } finally {
      setFiring(false);
    }
  }, [workflowId, client, analytics]);

  const breadcrumbs = useResourceBreadcrumbs({
    resourceType: ResourceType.Workflow,
    resourceId: workflowId,
    resourceName: trimmedName !== '' ? trimmedName : initialName,
    listBreadcrumb: { label: 'Triggers', href: `/${org.slug}/triggers` },
  });

  const triggerActions = (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={handleFire}
        disabled={firing}
      >
        {firing ? 'Firing...' : 'Trigger now'}
      </Button>
      <Button
        variant="destructive"
        size="sm"
        onClick={() => void archiveAction.execute({})}
      >
        Archive
      </Button>
      <Button size="sm" onClick={handleSave} disabled={saving || !canSave}>
        {saving ? 'Saving...' : 'Save'}
      </Button>
    </div>
  );

  return (
    <ResourcePageShell
      breadcrumbs={breadcrumbs}
      // The workflow, not the trigger row that led here: this page edits the
      // scheduled run, and its breadcrumb names the workflow too. Beside that
      // name rather than in the actions cluster; see the agent page.
      titleAdornment={
        <FavoriteToggleButton
          type={ResourceType.Workflow}
          id={workflowId}
          className="h-6 w-6"
          iconClassName="h-3.5 w-3.5"
        />
      }
      actions={triggerActions}
      surface="editor"
    >
      <div className="h-full bg-background overflow-auto">
        <div className="max-w-2xl mx-auto py-10 px-4">
          <div className="flex flex-col gap-6">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Name
              </label>
              <Input
                className="bg-white"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Give this trigger a name..."
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Agent
              </label>
              <Select value={agentId} onValueChange={setAgentId}>
                <SelectTrigger className="bg-white w-full">
                  <SelectValue placeholder="Select an agent" />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.title !== null && a.title.trim() !== ''
                        ? a.title
                        : a.agentKey}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Prompt (optional)
              </label>
              <Input
                className="bg-white"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Enter a prompt to send to the agent..."
              />
            </div>

            <SchedulePicker value={schedule} onChange={setSchedule} />
          </div>
        </div>
      </div>
    </ResourcePageShell>
  );
};

export const TriggerDetailPage: React.FC = () => {
  const { triggerId: workflowId } = useParams<{ triggerId: string }>();
  const { data: workflow, isLoading } = useScheduledWorkflow(workflowId ?? '');

  useTracking(CocoEvent.TriggerViewed, { triggerId: workflowId ?? null });

  if (isLoading || workflow === undefined) {
    return (
      <div className="h-full w-full bg-background">
        <div className="max-w-2xl mx-auto py-10 px-4">
          <Text.P className="text-muted-foreground">Loading...</Text.P>
        </div>
      </div>
    );
  }

  const schedule = workflow.schedule;
  if (schedule === undefined || schedule === '') {
    return (
      <div className="h-full w-full bg-background">
        <div className="max-w-2xl mx-auto py-10 px-4">
          <Text.P className="text-muted-foreground">
            This workflow does not have a cron schedule (open it from Triggers
            for scheduled agent runs).
          </Text.P>
        </div>
      </div>
    );
  }

  const initialPrompt =
    workflow.prompt !== undefined && workflow.prompt !== ''
      ? workflow.prompt
      : '';

  return (
    <ModelScopeFactory
      createModels={() => [new ResourceSelectionModel()]}
      deps={[workflow.id, workflow.triggerResourceId, workflow.name]}
    >
      <TriggerDetailResourceSelectionSync
        triggerResourceId={workflow.triggerResourceId}
        triggerName={workflow.name}
      />
      <TriggerDetailLoaded
        key={workflow.id}
        workflowId={workflow.id}
        initialName={workflow.name}
        initialSchedule={schedule}
        initialAgentId={workflow.agentId ?? ''}
        initialPrompt={initialPrompt}
      />
    </ModelScopeFactory>
  );
};
